import React, { FC, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { endSupportScreenShareTemplate } from 'apollo/templates/support-screen-share';
import { useScreenShareViewer, ViewerStatus } from './use-screen-share-viewer';

const STATUS_LABEL: Record<Exclude<ViewerStatus, 'WATCHING'>, string> = {
    CONNECTING: 'SCREEN_SHARE_CONNECTING',
    WAITING_FOR_SCREEN: 'SCREEN_SHARE_WAITING_FOR_SCREEN',
    RECONNECTING: 'SCREEN_SHARE_RECONNECTING',
    ENDED: 'SCREEN_SHARE_ENDED',
    ERROR: 'SCREEN_SHARE_ERROR',
};

/** Watch-only view of one customer's screen (DD-3715). */
const SupportScreenShareViewer: FC = () => {
    const { t: translate } = useTranslation('super-admin');
    const { showBoundary } = useDenthubErrorBoundary();
    const navigate = useNavigate();
    const { code = '' } = useParams();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const { status, share, errorMessage } = useScreenShareViewer(code, videoRef);
    const [endSupportScreenShare] = useMutation(endSupportScreenShareTemplate, {
        onError: (error) => showBoundary(error),
        onCompleted: () => navigate('/screen-share'),
    });

    const severity = status === 'ERROR' ? 'error' : status === 'RECONNECTING' ? 'warning' : 'info';

    return (
        <Box>
            <PageTitle
                headingStyle="h1"
                title={share ? `${share.userName} – ${share.organizationName}` : translate('MENU_SCREEN_SHARE')}
            />
            <Stack direction="row" spacing={2} mb={2} alignItems="center">
                <Typography>{translate('SCREEN_SHARE_CODE_LABEL', { code })}</Typography>
                <Box flexGrow={1} />
                <Button variant="text" onClick={() => videoRef.current?.requestFullscreen()}>
                    {translate('SCREEN_SHARE_FULLSCREEN')}
                </Button>
                <Button variant="text" onClick={() => navigate('/screen-share')}>
                    {translate('SCREEN_SHARE_LEAVE')}
                </Button>
                <Button variant="contained" onClick={() => setIsConfirmOpen(true)} disabled={status === 'ENDED'}>
                    {translate('SCREEN_SHARE_END')}
                </Button>
            </Stack>
            {status !== 'WATCHING' ? (
                <Alert severity={severity} icon={status === 'CONNECTING' ? <CircularProgress size={20} /> : undefined}>
                    {translate(STATUS_LABEL[status])}
                    {errorMessage ? ` (${errorMessage})` : ''}
                </Alert>
            ) : null}
            <Box
                component="video"
                ref={videoRef}
                autoPlay
                playsInline
                muted
                aria-label={translate('SCREEN_SHARE_VIDEO_LABEL')}
                sx={{
                    display: status === 'WATCHING' || status === 'RECONNECTING' ? 'block' : 'none',
                    width: '100%',
                    maxHeight: 'calc(100vh - 220px)',
                    objectFit: 'contain',
                    backgroundColor: 'black',
                    mt: 1,
                }}
            />
            <ConfirmDialog
                open={isConfirmOpen}
                dialogTitle={translate('SCREEN_SHARE_END')}
                dialogContent={translate('SCREEN_SHARE_END_CONFIRM')}
                handleClose={() => setIsConfirmOpen(false)}
                handleConfirm={() => {
                    setIsConfirmOpen(false);
                    endSupportScreenShare({ variables: { code } });
                }}
            />
        </Box>
    );
};

export default SupportScreenShareViewer;
